import { validationProblemsTag } from '../../../apis/apiValidationProblems';
import {
  ApiOperationType,
  EntityListTypeName,
  Operation,
  ProductListConfigurationRequest,
  ProductListConfigurationResponse,
  ProductResponse,
  SortDirection,
  ValidationArea,
} from '@ydistri/api-sdk';
import { apiSlice, TemplateOrCalculation } from '../../../apis/api';
import {
  CalculationsCollection,
  ProductListsCollection,
  TemplatesCollection,
} from '../../../swagger/collections';
import {
  SignalConfigurationChanged,
  SignalConfigurationsChanged,
} from '../../../signalr/signalrInterfaces';
import { signalrClient } from '../../../signalr/client';
import {
  forceRefetchForInfiniteScroll,
  InfiniteScrollParams,
  mergeForInfiniteScroll,
  serializeFullApiBasedQueryArgs,
  serializeQueryArgsForInfiniteScroll,
} from '@ydistri/ds';
import { getTags } from '../../../apis/apiLib';
import { SignalProjectConfigurationChanged } from '../../ProjectAdministration/common/administrationItemsTypes';
import { addToast } from '@ydistri/utils';
import { ReduxState } from '../../../store';
import { getSelectedProjectShortName } from '../../../apis/apiHeader';

const { TAGS, TAGS_ARRAY } = getTags('productListConfiguration');

export interface GetProductListConfigurationRequest extends InfiniteScrollParams {
  templateOrCalculation: TemplateOrCalculation;
}

export interface UpdateProductListConfigurationPayload {
  templateOrCalculation: TemplateOrCalculation;
  productListId: number;
  request: ProductListConfigurationRequest;
}

export interface GetProductListProductsPayload {
  productListId: number;
  entityListVersionId?: number;
  /**
   * Client-side only cache-bucket discriminator (not sent to the API). `serializeQueryArgsForInfiniteScroll`
   * keys the RTK Query cache by endpointName + projectShortName + this `instance`, so a distinct value per
   * `productListId:entityListVersionId` gives each list/version its own infinite-scroll bucket and prevents
   * pages from being merged across different lists or versions.
   */
  instance?: string;
  top?: number;
  skip?: number;
  search?: string;
}

export interface DeleteProductListConfigurationPayload {
  templateOrCalculation: TemplateOrCalculation;
  productListId: number;
}

/**
 * Sorter for product list configuration object. Objects are sorted by isConfigured first
 * than by name.
 * @param left
 * @param right
 */
const productListConfigSort = (
  left: ProductListConfigurationResponse,
  right: ProductListConfigurationResponse,
): number => {
  if (left.isConfigured && !right.isConfigured) {
    return -1;
  } else if (!left.isConfigured && right.isConfigured) {
    return 1;
  } else {
    if (left.name !== null) {
      return (left.name ?? '').localeCompare(right.name ?? '');
    } else {
      return 1;
    }
  }
};

export const apiProductLists = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getProductListConfiguration: builder.query<
        ProductListConfigurationResponse[], //Expanded
        GetProductListConfigurationRequest
      >({
        queryFn: async arg => {
          const query = {
            top: arg.top,
            skip: arg.skip,
            inlineCount: true,
            sortings: [
              { fieldName: 'IsConfigured', direction: SortDirection.Desc },
              ...(arg.sortings ?? []),
            ],
            conditions: arg.conditions,
            search: arg.search,
          };

          const {
            data: { data: productListConfiguration },
          } = await (arg.templateOrCalculation.type === 'Template'
            ? TemplatesCollection.getTemplateProductListConfigurations(
                arg.templateOrCalculation.id,
                query,
              )
            : CalculationsCollection.getCalculationProductListsV2(
                arg.templateOrCalculation.id,
                query,
              ));

          return { data: productListConfiguration.filter(pl => pl.isConfigured || !pl.isArchived) };
        },
        providesTags: (result, error, arg) => [
          { type: TAGS.productListConfiguration, id: JSON.stringify(arg) },
        ],
        serializeQueryArgs: serializeFullApiBasedQueryArgs(getSelectedProjectShortName),
        merge: mergeForInfiniteScroll<
          ProductListConfigurationResponse,
          GetProductListConfigurationRequest
        >('productListId'),
        forceRefetch: forceRefetchForInfiniteScroll<
          GetProductListConfigurationRequest | undefined
        >(),
        async onCacheEntryAdded(
          { templateOrCalculation: { type: objectType, id: selectedTemplateId } },
          api,
        ) {
          try {
            await api.cacheDataLoaded;

            /**
             * The cached arguments that hold the notified template's rows - and only those. Product
             * lists are project-wide, so the same productListId lives in every template's cache (and
             * in a calculation's): a notification for template A must never rewrite template B's rows.
             * The project guard keeps the tab's project isolation (the cache is keyed by it).
             */
            const cachedArgsOfTemplate = (
              signal: SignalConfigurationChanged | SignalConfigurationsChanged,
            ): GetProductListConfigurationRequest[] => {
              if (signal.projectSlug !== getSelectedProjectShortName()) return [];
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the state is ReduxState
              const state = api.getState() as ReduxState;
              return apiProductLists.util
                .selectCachedArgsForQuery(state, 'getProductListConfiguration')
                .filter(
                  qk =>
                    qk.templateOrCalculation.type === 'Template' &&
                    qk.templateOrCalculation.id === signal.templateId,
                );
            };

            const listener = (signal: SignalConfigurationChanged) => {
              // eslint-disable-next-line no-console -- we want to output the signal
              console.log('CONFIGURATION CHANGED', signal);
              if (selectedTemplateId !== signal.templateId) return;
              if (
                signal.entityType === 'ProductListConfiguration' &&
                signal.operationType === ApiOperationType.Patch
              ) {
                const productListId = signal.entityId;

                cachedArgsOfTemplate(signal).forEach(qk => {
                  api.dispatch(
                    apiProductLists.util.updateQueryData(
                      'getProductListConfiguration',
                      qk,
                      draft => {
                        const productList = draft.find(r => r.productListId === productListId);
                        if (!productList) return;
                        // Field-aware: a pin-only advance changes productListVersionId and nothing else.
                        signal.updates.forEach(update => {
                          if (update.value === undefined || update.value === null) return;
                          switch (update.fieldName) {
                            case 'isEnabled':
                              productList.isEnabled = update.value === '1';
                              productList.isConfigured = true;
                              break;
                            case 'productListVersionId': {
                              const pinned = parseInt(update.value);
                              if (pinned !== productList.productListVersionId) {
                                // A writer pins a usable list's latest version: healthy until the reread below says otherwise.
                                productList.isDataDeleted = false;
                                productList.dataDeletedAt = null;
                              }
                              productList.productListVersionId = pinned;
                              productList.isConfigured = true;
                              break;
                            }
                          }
                        });
                      },
                    ),
                  );
                });

                // The data-deleted pair belongs to the pinned version, so the row is read again for it. Cache keys carry
                // the selected project: a reread that returns after a project switch would land in the next project's
                // entries, so it is dropped and the assumed pair above stays. A pin that moved on again keeps the later pair.
                if (
                  objectType === 'Template' &&
                  signal.updates.some(u => u.fieldName === 'productListVersionId' && u.value)
                ) {
                  const project = getSelectedProjectShortName();
                  TemplatesCollection.getTemplateProductListConfigurations(signal.templateId, {
                    conditions: [
                      { fieldName: 'ProductListId', operation: Operation.Eq, value: productListId },
                    ],
                  }).then(response => {
                    if (getSelectedProjectShortName() !== project) return;
                    const reread = response.data.data.find(r => r.productListId === productListId);
                    if (!reread) return;
                    cachedArgsOfTemplate(signal).forEach(qk => {
                      api.dispatch(
                        apiProductLists.util.updateQueryData(
                          'getProductListConfiguration',
                          qk,
                          draft => {
                            const productList = draft.find(r => r.productListId === productListId);
                            if (productList?.productListVersionId !== reread.productListVersionId)
                              return;
                            productList.isDataDeleted = reread.isDataDeleted;
                            productList.dataDeletedAt = reread.dataDeletedAt;
                          },
                        ),
                      );
                    });
                  });
                }
              }
            };

            const listenerForDeletes = (signals: SignalConfigurationsChanged[]) => {
              // eslint-disable-next-line no-console -- we want to output the signals
              console.log('CONFIGURATIONS CHANGED [checking delete]', signals);
              signals.forEach(signal => {
                if (selectedTemplateId !== signal.templateId) return;
                const productListId = signal.entityId;
                if (signal.entityType === 'ProductListConfiguration') {
                  if (signal.operationType === ApiOperationType.Delete) {
                    cachedArgsOfTemplate(signal).forEach(qk => {
                      api.dispatch(
                        apiProductLists.util.updateQueryData(
                          'getProductListConfiguration',
                          qk,
                          draft => {
                            const pl = draft.find(r => r.productListId === productListId);
                            if (pl) {
                              pl.isEnabled = null;
                              pl.isConfigured = false;
                            }
                          },
                        ),
                      );
                    });
                  }
                }
              });
            };

            const listenerForProjectConfiguration = (signal: SignalProjectConfigurationChanged) => {
              if (
                !(
                  signal.entityType === 'EntityList' &&
                  signal.entityListTypeNames.includes(EntityListTypeName.Product)
                )
              ) {
                return;
              }

              // eslint-disable-next-line no-console -- we want this output
              console.log('Project configuration CHANGED', signal);

              switch (signal.operationType) {
                case ApiOperationType.Create:
                case ApiOperationType.Replace:
                case ApiOperationType.Patch:
                  //when entry changes or is created, we need to update the cache

                  TemplatesCollection.getTemplateProductListConfigurations(selectedTemplateId, {
                    conditions: [
                      {
                        fieldName: 'ProductListId',
                        operation: Operation.Eq,
                        value: signal.entityId,
                      },
                    ],
                  }).then(response => {
                    api.updateCachedData(draft => {
                      response.data.data.forEach(productListConfiguration => {
                        const plIndex = draft.findIndex(
                          r => r.productListId === productListConfiguration.productListId,
                        );
                        if (plIndex > -1) {
                          //if the product list was archived by the patch, we need to remove it from the list (but only
                          //if it was not configured)
                          if (
                            productListConfiguration.isArchived &&
                            !draft[plIndex].isArchived &&
                            !draft[plIndex].isConfigured
                          ) {
                            draft.splice(plIndex, 1);
                            api.dispatch(
                              addToast({
                                message: `Product List '${productListConfiguration.name}' has been archived and cannot be used.`,
                              }),
                            );
                          } else {
                            draft[plIndex] = productListConfiguration;
                          }
                        } else {
                          //insert the item where it belongs in the sorted data
                          //the sorted items may have sorting values like this [-1, -1, -1, 1, 1, 1]
                          //we need to find the first index of 1, that is where the new item belongs
                          const insertionIndex = draft.findIndex(
                            existingItem =>
                              productListConfigSort(existingItem, productListConfiguration) === 1,
                          );

                          //either append the item to the end or insert it at the sorted position
                          if (insertionIndex === -1) {
                            draft.push(productListConfiguration);
                          } else {
                            draft.splice(insertionIndex, 0, productListConfiguration);
                          }

                          api.dispatch(
                            addToast({
                              message: `New Product List '${productListConfiguration.name}' has been added to the list.`,
                            }),
                          );
                        }
                      });
                    });
                  });
                  break;
                case ApiOperationType.Delete:
                  api.updateCachedData(draft => {
                    const indexToDelete = draft.findIndex(
                      pl => pl.productListId === signal.entityId,
                    );
                    if (indexToDelete > -1) {
                      const plName = draft[indexToDelete].name;
                      draft.splice(indexToDelete, 1);
                      api.dispatch(
                        addToast({
                          message: `Product List '${plName}' has been deleted.`,
                        }),
                      );
                    }
                  });
                  break;
                case ApiOperationType.Read: {
                  //we are not interested in reads
                }
              }
            };

            signalrClient.on('TemplateConfigurationChanged', listener);
            signalrClient.on('TemplateConfigurationsChanged', listenerForDeletes);

            //we listen for changes only when a template is open, not calculation
            if (objectType === 'Template') {
              signalrClient.on('ProjectConfigurationChanged', listenerForProjectConfiguration);
            }

            api.cacheEntryRemoved.then(() => {
              signalrClient.off('TemplateConfigurationChanged', listener);
              signalrClient.off('TemplateConfigurationsChanged', listenerForDeletes);
              if (objectType === 'Template') {
                signalrClient.off('ProjectConfigurationChanged', listenerForProjectConfiguration);
              }
            });
          } catch {
            // no-op in case cache entry was removed before data was loaded
          }
          await api.cacheEntryRemoved;
        },
      }),

      updateProductListConfiguration: builder.mutation<
        ProductListConfigurationResponse,
        UpdateProductListConfigurationPayload
      >({
        invalidatesTags: (result, error, { templateOrCalculation }) => [
          validationProblemsTag(templateOrCalculation, ValidationArea.ProductLists),
        ],
        queryFn: async payload => {
          const {
            data: { data: productListConfiguration },
          } = await TemplatesCollection.postTemplateProductListConfiguration(
            payload.templateOrCalculation.id,
            payload.productListId,
            payload.request,
          );

          return { data: productListConfiguration };
        },
        onQueryStarted(payload, api) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the state is ReduxState
          const state = api.getState() as ReduxState;
          const queryKeys = apiProductLists.util.selectCachedArgsForQuery(
            state,
            'getProductListConfiguration',
          );

          queryKeys.forEach(qk => {
            const patchResult = api.dispatch(
              apiProductLists.util.updateQueryData('getProductListConfiguration', qk, draft => {
                const productList = draft.find(r => r.productListId === payload.productListId);
                if (productList)
                  Object.assign(productList, {
                    isEnabled: payload.request.isEnabled,
                    isConfigured: true,
                  });
              }),
            );
            api.queryFulfilled.catch(patchResult.undo);
          });
        },
      }),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response sent
      deleteProductListConfiguration: builder.mutation<void, DeleteProductListConfigurationPayload>(
        {
          invalidatesTags: (result, error, { templateOrCalculation }) => [
            validationProblemsTag(templateOrCalculation, ValidationArea.ProductLists),
          ],
          queryFn: async payload => {
            await TemplatesCollection.deleteTemplateProductListConfiguration(
              payload.templateOrCalculation.id,
              payload.productListId,
            );
            return { data: undefined };
          },
          onQueryStarted(payload, api) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the state is ReduxState
            const state = api.getState() as ReduxState;
            const queryKeys = apiProductLists.util.selectCachedArgsForQuery(
              state,
              'getProductListConfiguration',
            );

            queryKeys.forEach(qk => {
              const patchResult = api.dispatch(
                apiProductLists.util.updateQueryData('getProductListConfiguration', qk, draft => {
                  const productList = draft.find(r => r.productListId === payload.productListId);
                  if (productList)
                    Object.assign(productList, {
                      isEnabled: null,
                      isConfigured: false,
                    });
                }),
              );
              api.queryFulfilled.catch(patchResult.undo);
            });
          },
        },
      ),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response sent
      resetProductListConfiguration: builder.mutation<void, TemplateOrCalculation>({
        invalidatesTags: (result, error, arg) => [
          validationProblemsTag(arg, ValidationArea.ProductLists),
        ],
        queryFn: async payload => {
          await TemplatesCollection.deleteTemplateProductListConfigurations(payload.id);
          return { data: undefined };
        },
        onQueryStarted(payload, api) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the state is ReduxState
          const state = api.getState() as ReduxState;
          const queryKeys = apiProductLists.util.selectCachedArgsForQuery(
            state,
            'getProductListConfiguration',
          );

          queryKeys.forEach(qk => {
            const patchResult = api.dispatch(
              apiProductLists.util.updateQueryData('getProductListConfiguration', qk, draft => {
                draft.forEach(b => {
                  if (b.isConfigured) b.isConfigured = false;
                });
              }),
            );
            api.queryFulfilled.catch(patchResult.undo);
          });
        },
      }),

      getProductListProducts: builder.query<
        ProductResponse[], //Expanded
        GetProductListProductsPayload
      >({
        queryFn: async payload => {
          const queryParams = {
            top: payload.top,
            skip: payload.skip ?? 0,
            inlineCount: true,
            search: payload.search,
            sortings: [{ fieldName: 'Name', direction: SortDirection.Asc }],
          };

          const { data } =
            payload.entityListVersionId && payload.entityListVersionId > 0
              ? await ProductListsCollection.getProductsForVersion(
                  payload.productListId,
                  payload.entityListVersionId,
                  queryParams,
                )
              : await ProductListsCollection.getProducts(payload.productListId, queryParams);

          return { data: data.data };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.productListConfigurationProducts,
            id: `plp-${arg.productListId}-${arg.entityListVersionId ?? 'latest'}`,
          },
        ],
        serializeQueryArgs: serializeQueryArgsForInfiniteScroll<GetProductListProductsPayload>(
          getSelectedProjectShortName,
        ),
        merge: mergeForInfiniteScroll<ProductResponse, GetProductListProductsPayload>(),
        forceRefetch: forceRefetchForInfiniteScroll<GetProductListProductsPayload | undefined>(),
      }),
    }),
  });

export const {
  useGetProductListConfigurationQuery,
  useUpdateProductListConfigurationMutation,
  useDeleteProductListConfigurationMutation,
  useResetProductListConfigurationMutation,
  useGetProductListProductsQuery,
} = apiProductLists;
